import type { HgClubInfo } from '@ps/web-shared-api-types/v0/SwHomeGames';
import classnames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { RootState } from '@swf/core/store';
import {
  GameList,
  GameTile,
  GameViewToggle,
  Image,
  PKR_INITIAL_LOCAL_STORAGE,
  useHomeGamesWidget
} from '@swf/poker';

import type { GamesByClubs, TileType } from '../../types/common';
import { GameView } from '../../types/common';
import DeleteGameButton from '../GameTile/DeleteGame/DeleteGameButton';
import GameTileSkeleton from '../Shared/GameTileSkeleton/GameTileSkeleton';

import styles from './PokerGameTileContainer.scss';

export interface Props {
  games: GamesByClubs;
  clubs: HgClubInfo[];
  tileType: TileType;
  loading?: boolean;
  isCashGamesActive?: boolean;
  isTournsActive?: boolean;
}

const PokerGameTileContainer: React.FC<Props> = ({
  games,
  clubs,
  tileType,
  loading,
  isCashGamesActive,
  isTournsActive
}) => {
  const { widgetSelect } = useHomeGamesWidget();
  const isMobileDevice = useSelector(select.device.isMobile);

  const gameTileView =
    useSelector((rootState: RootState) =>
      widgetSelect.localStorageItem(rootState, 'gameTileView')
    ) ?? PKR_INITIAL_LOCAL_STORAGE.gameTileView;

  return (
    <section
      className={classnames(styles.container, { [styles.mobileClickFeedback]: isMobileDevice })}
    >
      {Object.entries(games).map(([clubId, games]) => {
        const club = clubs.find((club) => club.clubId && club.clubId === Number(clubId));
        const hasGames = games.find(({ clubId }) => clubId === club?.clubId);
        const toggleId = `${clubId}-${club?.clubName}`;
        const showListView = gameTileView?.[toggleId] === GameView.List;

        return (
          <div className={styles.gamesOuterContainer} key={clubId}>
            {hasGames && (
              <div className={styles.title}>
                <div className={styles.clubAvatar}>
                  <Image className={styles.avatar} imageByteArray={club?.clubImage} />
                </div>
                <p>{club?.clubName}</p>
                <DeleteGameButton games={games} />
                {!isMobileDevice && <GameViewToggle id={toggleId} />}
              </div>
            )}
            {loading ? (
              <GameTileSkeleton
                games={games}
                type={gameTileView?.[toggleId] ?? (GameView.Grid as GameView)}
              />
            ) : showListView ? (
              <div>
                <GameList games={games} tileType={tileType} clubId={clubId} />
              </div>
            ) : (
              <div
                className={styles.gamesInnerContainer}
                data-testid={`${club?.clubName}-game-container`}
              >
                {games.map((game) => (
                  <GameTile
                    key={game.id}
                    {...game}
                    isTournsActive={isTournsActive}
                    isCashGamesActive={isCashGamesActive}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

export default PokerGameTileContainer;
